import React, { useState } from "react";
import Heading from "./Heading";

export interface ITabContent {
  tabTitle: string;
  tabId?: string;
  content: React.ReactNode;
  contentId?: string;
  contentClassName?: string;
}

export enum ETabListLayout {
  center = "center",
  left = "left",
}

export interface ITabbedContentBladeProps {
  className?: string;
  title?: string;
  titleClassName?: string;
  titleHeadingLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
  secondaryTitle?: string;
  secondaryTitleClassName?: string;
  tabListClassName?: string;
  tabListAriaLabel?: string;
  tabListLayout?: ETabListLayout;
  tabListItemClassNames?: { default?: string; active?: string };
  tabs: ITabContent[];
  onTabChange?: (tabIndex: number) => any;
}

export const TabbedContentBlade: React.FC<ITabbedContentBladeProps> = ({
  className,
  title,
  titleClassName,
  titleHeadingLevel = "h1",
  secondaryTitle,
  secondaryTitleClassName,
  tabListClassName,
  tabListAriaLabel,
  tabListLayout = ETabListLayout.center,
  tabListItemClassNames = {
    default: "border-b",
    active: "border-b-4 font-bold",
  },
  tabs,
  onTabChange,
}) => {
  const [activeTabIndex, setActiveTab] = useState(0);

  const handleTabClick = (tabIndex: number) => {
    setActiveTab(tabIndex);
    onTabChange && onTabChange(tabIndex);
  };

  const keydownEventListener = (event: React.KeyboardEvent) => {
    var key = event.key;
    switch (key) {
      case keys.end:
        event.preventDefault();
        // Activate last tab
        focusTabByIndex(tabs.length - 1);
        break;
      case keys.home:
        event.preventDefault();
        // Activate first tab
        focusTabByIndex(0);
        break;
    }
  };

  const keyupEventListener = (event: React.KeyboardEvent) => {
    var key = event.key;
    switch (key) {
      case keys.left:
        focusPrevTab();
        break;

      case keys.right:
        focusNextTab();
        break;
    }
  };

  const focusNextTab = () => {
    var nextIndex = activeTabIndex + 1;
    if (nextIndex > tabs.length - 1) {
      focusTabByIndex(0);
    } else {
      focusTabByIndex(nextIndex);
    }
  };

  const focusPrevTab = () => {
    var prevIndex = activeTabIndex - 1;
    if (prevIndex < 0) {
      focusTabByIndex(tabs.length - 1);
    } else {
      focusTabByIndex(prevIndex);
    }
  };

  const focusTabByIndex = (index: number) => {
    setActiveTab(index);
    getTabElementByIndex(index)?.focus();
    onTabChange && onTabChange(index);
  };

  const getTabElementByIndex = (index: number) => {
    var tabId = tabs[index].tabId;
    var tabElement = document.getElementById(tabId ? tabId : `tab_${index}`);
    return tabElement;
  };

  const keys = {
    end: "End",
    home: "Home",
    left: "ArrowLeft",
    right: "ArrowRight",
  };

  let tabListLayoutClassNames = "";
  let tabListItemLayoutClassNames = "";
  switch (tabListLayout) {
    case ETabListLayout.left:
      tabListLayoutClassNames =
        "justify-around md:justify-evenly lg:justify-start";
      tabListItemLayoutClassNames = "w-auto lg:mr-12";
      break;
    default:
      tabListLayoutClassNames = "md:px-4 lg:px-14 justify-center";
      tabListItemLayoutClassNames = "w-3/4 md:w-1/2 lg:w-2/5";
      break;
  }

  return (
    <section
      className={`tabbed-content-blade ${className} flex flex-col items-center`}
    >
      {title && (
        <Heading className={titleClassName} headingLevel={titleHeadingLevel}>
          {title}
          {secondaryTitle && (
            <span className={secondaryTitleClassName}>{secondaryTitle}</span>
          )}
        </Heading>
      )}
      <ul
        role="tablist"
        aria-label={tabListAriaLabel}
        className={`list-none ${tabListClassName} w-full py-4 ${tabListLayoutClassNames} flex flex-wrap md:flex-nowrap`}
      >
        {tabs.map((item: ITabContent, index: number) => (
          <li
            key={index}
            className={`text-center ${tabListItemLayoutClassNames} border-solid 
              ${
                activeTabIndex == index
                  ? tabListItemClassNames.active
                  : `cursor-pointer ${tabListItemClassNames.default}`
              }`}
            onClick={() => handleTabClick(index)}
            onKeyDown={(e) => keydownEventListener(e)}
            onKeyUp={(e) => keyupEventListener(e)}
            role="tab"
            aria-selected={activeTabIndex == index}
            tabIndex={activeTabIndex == index ? 0 : -1}
            aria-controls={item.tabId ? item.tabId : `tabpanel_${index}`}
            id={item.tabId ? item.tabId : `tab_${index}`}
          >
            {item.tabTitle}
          </li>
        ))}
      </ul>
      {tabs.map((item: ITabContent, index: number) => (
        <div
          key={index}
          className={`${activeTabIndex !== index ? "hidden" : ""} ${
            item.contentClassName
          }`}
          role="tabpanel"
          tabIndex={0}
          aria-labelledby={item.tabId ? item.tabId : `tab_${index}`}
          id={item.contentId ? item.contentId : `tabpanel_${index}`}
        >
          {item.content}
        </div>
      ))}
    </section>
  );
};

export default TabbedContentBlade;
